import type { LogbookEntriesQuery } from "~/graphql/generated";
import { AVATAR_PALETTE } from "./expedition-constants";

// ── Crew / avatar ─────────────────────────────────────────────────────────────

export function roleCls(role: string | null | undefined): string {
  const ROLE_CLS: Record<string, string> = {
    SKIPPER: "role-pill role-skipper",
    FIRST_MATE: "role-pill role-firstmate",
    CREW: "role-pill role-crew",
    STUDENT: "role-pill role-student",
    OBSERVER: "role-pill role-observer",
  };
  if (!role) return "role-pill role-crew";
  return ROLE_CLS[role.toUpperCase()] ?? "role-pill role-crew";
}

export function roleAvatarClass(role: string | null | undefined): string {
  const MAP: Record<string, string> = {
    SKIPPER: "av-role-skipper",
    FIRST_MATE: "av-role-firstmate",
    WATCH: "av-role-watch",
    CREW: "av-role-crew",
    STUDENT: "av-role-crew",
    OBSERVER: "av-role-crew",
  };
  if (!role) return "av-role-crew";
  return MAP[role.toUpperCase()] ?? "av-role-crew";
}

export function toDetailCrewRole(role: string | null | undefined): "skipper" | "firstmate" | "watch" | "crew" {
  const MAP: Record<string, "skipper" | "firstmate" | "watch" | "crew"> = {
    SKIPPER: "skipper",
    FIRST_MATE: "firstmate",
    WATCH: "watch",
  };
  if (!role) return "crew";
  return MAP[role.toUpperCase()] ?? "crew";
}

export function avatarColor(id: string): { bg: string; color: string } {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (
    AVATAR_PALETTE[h % AVATAR_PALETTE.length] ?? {
      bg: "#e0f2fe",
      color: "#0369a1",
    }
  );
}

export function nameInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ── Expenses ──────────────────────────────────────────────────────────────────

export function expenseRecorderInitials(u: { displayName: string; firstName: string; lastName: string }): string {
  const name = u.displayName || `${u.firstName} ${u.lastName}`;
  return nameInitials(name);
}

// ── Logbook ───────────────────────────────────────────────────────────────────

type LogbookEntryRow = NonNullable<LogbookEntriesQuery["logbookEntries"]>[number];

export function fmtEntryTimeUtc(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

export function fmtEntryDayUtc(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function groupEntriesByDay(entries: LogbookEntryRow[]): [string, LogbookEntryRow[]][] {
  const map = new Map<string, LogbookEntryRow[]>();
  for (const e of entries) {
    const day = e.entryTime.slice(0, 10);
    const list = map.get(day) ?? [];
    list.push(e);
    map.set(day, list);
  }
  return [...map.entries()];
}

export function logAuthorInitials(author: LogbookEntryRow["author"]): string {
  const name = author.displayName || `${author.firstName} ${author.lastName}`;
  return nameInitials(name);
}
